import { useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import {
  Box,
  Container,
  Card,
  Grid,
  Typography,
  Button,
} from "@material-ui/core";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderClientes from "components/_refactor/headers/clients";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import FromToDateField from "components/_refactor/fields/from-to-date";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { IsMobile } from "utils/is-mobile";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

export default function FranchiseNetworkRanking() {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [data, setData] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [initialDate, setInitialDate] = useState(
    moment().startOf("month").format("DD/MM/YYYY")
  );
  const [finalDate, setFinalDate] = useState(
    moment(new Date()).format("DD/MM/YYYY")
  );
  const isMobile = IsMobile();

  const getTableColumns = () => [
    {
      dataField: "Posicao",
      text: "Posição",
      sort: true,
      formatter: (posicao) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: "inherit",
          }}
        >
          {posicao}
        </Typography>
      ),
    },
    {
      dataField: "Loja",
      text: "Loja",
      sort: true,
      formatter: (loja) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: "inherit",
          }}
        >
          {loja}
        </Typography>
      ),
    },
    {
      dataField: "FaturamentoMensal",
      text: "Faturamento (Mês)",
      sort: true,
      formatter: (faturamento) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: "inherit",
          }}
        >
          R$ {MaskAndValid.maskPrice(faturamento)}
        </Typography>
      ),
    },
    {
      dataField: "TicketMedio",
      text: "Ticket Médio",
      sort: true,
      formatter: (ticketMedio) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: "inherit",
          }}
        >
          R$ {MaskAndValid.maskPrice(ticketMedio)}
        </Typography>
      ),
    },
    {
      dataField: "Mes",
      text: "Mês",
      sort: true,
      formatter: (mes) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: "inherit",
          }}
        >
          {mes || "-"}
        </Typography>
      ),
    },
    {
      dataField: "Crescimento",
      text: "Crescimento (vs Mês Ant.)",
      sort: true,
      formatter: (crescimento) => (
        <Typography
          variant="body1"
          style={{
            fontWeight: "normal",
            color: crescimento > 0 ? "green" : "red",
          }}
        >{`${crescimento > 0 ? "+" : ""}${MaskAndValid.maskDecimal(
          crescimento,
          1
        )}%`}</Typography>
      ),
    },
  ];

  const fetchList = async () => {
    if (!initialDate || !finalDate) {
      snackbar.showMessage("Por favor, selecione o período inicial e final", {
        severity: "warning",
      });
      return;
    }

    setSearching(true);

    try {
      const dataInicioMoment = moment(initialDate, "DD/MM/YYYY");
      const dataFimMoment = moment(finalDate, "DD/MM/YYYY");

      if (!dataInicioMoment.isValid() || !dataFimMoment.isValid()) {
        snackbar.showMessage("Por favor, selecione datas válidas", {
          severity: "warning",
        });
        setSearching(false);
        return;
      }

      if (dataFimMoment.isBefore(dataInicioMoment, "day")) {
        snackbar.showMessage(
          "A data final deve ser maior ou igual à data inicial",
          {
            severity: "warning",
          }
        );
        setSearching(false);
        return;
      }

      const hoje = moment().startOf("day");
      if (dataFimMoment.isAfter(hoje, "day")) {
        snackbar.showMessage("A data final não pode ser maior que hoje", {
          severity: "warning",
        });
        setSearching(false);
        return;
      }

      if (dataInicioMoment.isAfter(hoje, "day")) {
        snackbar.showMessage("A data inicial não pode ser maior que hoje", {
          severity: "warning",
        });
        setSearching(false);
        return;
      }

      const dataInicio = dataInicioMoment.format("YYYY-MM-DD");
      const dataFim = dataFimMoment.format("YYYY-MM-DD");

      const parameter = `?dataInicio=${dataInicio}&dataFim=${dataFim}`;
      const response = await Api.get(
        "store-ranking",
        "franquias",
        parameter,
        true
      );

      if (response.Success && response.Result) {
        const allData = [];
        let currentStoreId = null;

        const periodoInicio = dataInicioMoment.startOf("month");
        const periodoFim = dataFimMoment.endOf("month");

        if (response.Result?.Items && response.Result.Items.length > 0) {
          const selectedStore = response.Result.Items[0];
          currentStoreId = selectedStore.IdPessoaLoja || null;
          setSelectedStoreId(currentStoreId);

          response.Result.Items.forEach((item, index) => {
            const defaultMonth = moment(
              initialDate || finalDate || new Date()
            ).format("MMMM/YYYY");
            allData.push({
              IdRankingDaRede: `current-${item.IdPessoaLoja || index}`,
              Mes: defaultMonth.toUpperCase().charAt(0) + defaultMonth.slice(1),
              Posicao: item.Posicao || "-",
              Loja: item.NomeLoja,
              FaturamentoMensal: item.FaturamentoMes || 0,
              TicketMedio: item.TicketMedio || 0,
              Crescimento: item.PercentualCrescimento || 0,
              IdPessoaLoja: item.IdPessoaLoja,
              LojaSelecionada: false,
            });
          });
        } else {
          setSelectedStoreId(null);
        }

        if (response.Result?.Meses && response.Result.Meses.length > 0) {
          const meses = response.Result.Meses;
          const mesesOrdenados = [...meses]
            .filter((mes) => {
              const mesMoment = moment(mes.Mes, "YYYY-MM").startOf("month");
              return (
                (mesMoment.isAfter(periodoInicio, "month") ||
                  mesMoment.isSame(periodoInicio, "month")) &&
                (mesMoment.isBefore(periodoFim, "month") ||
                  mesMoment.isSame(periodoFim, "month"))
              );
            })
            .sort((a, b) => {
              const mesA = moment(a.Mes, "YYYY-MM");
              const mesB = moment(b.Mes, "YYYY-MM");
              return mesB.diff(mesA);
            });

          mesesOrdenados.forEach((mes) => {
            if (mes.Items && mes.Items.length > 0) {
              mes.Items.forEach((item, index) => {
                allData.push({
                  IdRankingDaRede: `${mes.Mes}-${
                    item.IdPessoaLoja || item.Posicao || index
                  }`,
                  Mes: mes.MesFormat || mes.Mes,
                  Posicao: item.Posicao,
                  Loja: item.NomeLoja,
                  FaturamentoMensal: item.FaturamentoMes,
                  TicketMedio: item.TicketMedio,
                  Crescimento: item.PercentualCrescimento,
                  IdPessoaLoja: item.IdPessoaLoja,
                  LojaSelecionada: false,
                });
              });
            }
          });
        }

        if (allData.length > 0) {
          setData(allData);
        } else {
          setData([]);
          snackbar.showMessage(
            "Nenhum dado disponível para o período selecionado",
            {
              severity: "info",
            }
          );
        }
      } else {
        setData([]);
        setSelectedStoreId(null);
        if (
          response.Success &&
          (!response.Result ||
            !response.Result.Meses ||
            response.Result.Meses.length === 0)
        ) {
          snackbar.showMessage(
            "Nenhum dado disponível para o período selecionado",
            {
              severity: "info",
            }
          );
        }
      }
    } catch (error) {
      snackbar.showMessage(
        error?.message || error || "Erro ao buscar dados do ranking",
        {
          severity: "error",
        }
      );
      setData([]);
      setSelectedStoreId(null);
    } finally {
      setSearching(false);
    }
  };

  const styledRow = (row) => {
    if (row.LojaSelecionada) {
      return {
        backgroundColor: "#fff3cd",
        fontWeight: 600,
      };
    }

    return {};
  };

  const handleRefresh = async () => {
    return fetchList();
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes section="Desempenho Franquias" subsection="Dashboard" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <Grid
                container
                className="pl-4"
                style={{ padding: isMobile ? "0 24px 10px" : "24px" }}
              >
                <Grid item xs={12} className="mb-4">
                  <Box
                    component={Typography}
                    variant="h2"
                    marginBottom="0!important"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box component="span">Ranking da Rede</Box>
                    <IconButtonLoader
                      size="medium"
                      extraStyle={{
                        width: 24,
                        height: 24,
                        marginLeft: "0.3rem",
                        cursor: "pointer",
                      }}
                      title="Recarregar lista"
                      onClick={handleRefresh}
                      icon={<RefreshIcon color="primary" />}
                      aria-label="refresh"
                      color="primary"
                      disabled={searching}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <FromToDateField
                      label="Período"
                      initialDate={initialDate}
                      finalDate={finalDate}
                      setInitialDate={setInitialDate}
                      setFinalDate={setFinalDate}
                      disabled={searching}
                      validateMaxDate={true}
                      validateDateRange={true}
                    />
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleRefresh}
                        disabled={searching}
                        style={{ marginBottom: isMobile ? "0.5rem" : "0" }}
                      >
                        Pesquisar
                      </Button>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <ToolkitTable
                keyField="IdRankingDaRede"
                dataTable={data}
                columnModel={getTableColumns()}
                search
                allowEmpty
                noResultTitle=""
                noResultSubtitle="Nenhum dado disponível para o período selecionado."
                styledRow={styledRow}
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid
                      item
                      xs={isMobile ? 12 : 12}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar"
                        {...searchProps}
                        disabled={searching}
                      />
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
